/**
 * PlaybackProvider contract — Poletti Platform unified playback foundation.
 *
 * The Playback Host and PlaybackEngine speak ONLY through this contract.
 * Concrete adapters (HLS, MP4 progressive, YouTube embed, Vimeo embed, future
 * DRM / live / multi-cam) implement the same surface so providers stay
 * interchangeable without host conditionals.
 *
 * Surface kinds:
 *   - "video"  → attaches to HTMLVideoElement (HLS / MP4)
 *   - "embed"  → attaches to a container element and owns an iframe/SDK
 *
 * Required methods (all providers):
 *   getName() -> string
 *   getSurfaceKind() -> "video" | "embed"
 *   supports(source) -> boolean
 *   initialize() -> Promise
 *   attach(element) -> void
 *   detach() -> void
 *   load(source, options?) -> Promise
 *   destroy() -> void
 *
 * Transport (required for embed; optional for video — engine falls back to <video>):
 *   play() -> Promise
 *   pause() -> void
 *   seek(seconds) -> void
 *   setMuted(boolean) -> void
 *   setVolume(0..1) -> void
 *   getCurrentTime() -> number
 *   getDuration() -> number
 *   isPlaying() -> boolean
 *   isMuted() -> boolean
 *
 * Optional:
 *   getCapabilities() -> capability object (see ProviderCapabilities.js)
 *   stop(), enterFullscreen(), exitFullscreen()
 *   getLevels() / setLevel()
 *   getAudioTracks() / setAudioTrack()
 *   on(event, handler) / off(event, handler)
 *
 * Host/UI must use getCapabilities() / engine.hasCapability() — never
 * `if (provider === "youtube")` conditionals.
 *
 * Canonical provider events (emit via on/off): see StreamingProviderEvents +
 * PlaybackProviderEvents below. Engine normalizes these onto the Playback Event Bus.
 */

export var SurfaceKind = Object.freeze({
  VIDEO: "video",
  EMBED: "embed",
});

/**
 * Extended events for embed / future providers.
 * StreamingProviderEvents remain valid for ABR providers.
 */
export var PlaybackProviderEvents = Object.freeze({
  READY: "ready",
  PLAY: "play",
  PAUSE: "pause",
  PROGRESS: "progress",
  BUFFER: "buffer",
  SEEK: "seek",
  ENDED: "ended",
  ERROR: "error",
  TIME: "time",
  VOLUME: "volume",
  DESTROYED: "destroyed",
});

/**
 * Default getSurfaceKind for classic video-element providers.
 * @returns {"video"}
 */
export function videoSurfaceKind() {
  return SurfaceKind.VIDEO;
}

/**
 * @returns {"embed"}
 */
export function embedSurfaceKind() {
  return SurfaceKind.EMBED;
}

export default {
  SurfaceKind: SurfaceKind,
  PlaybackProviderEvents: PlaybackProviderEvents,
  videoSurfaceKind: videoSurfaceKind,
  embedSurfaceKind: embedSurfaceKind,
};
